import { Slide } from '../../../webit_modules/wb-barba-transitions/src';
import { setCookie } from './Cookies';
import barba from '@barba/core';
import barbaPrefetch from '@barba/prefetch';

export class WbBarbaController {
  constructor() {
    this.objects = [];
  }

  addObject(fn) {
    this.objects.push(fn);
  }

  init() {
    this.initBarbaRouting();
    this.initAllFunctions();
  }

  initFunction(fn) {
    return new Promise((resolve) => {
      fn.init().then(resolve);
    });
  }

  initAllFunctions() {
    const _this = this;

    return new Promise((resolve) => {
      Promise.all(_this.objects.map(_this.initFunction)).then(resolve);
    });
  }

  initBarbaRouting() {
    const _this = this;

    barba.use(barbaPrefetch);

    barba.init({
      timeout: 30000,
      debug: true, // Switch to false when compiling for prod
      schema: {
        prefix: 'data-transition',
      },
      /**
       * Lifecycle documentation - https://barba.js.org/docs/getstarted/lifecycle/
       */
      transitions: [
        {
          name: 'default',
          leave(data) {
            return new Promise((resolve) => {
              const transition = new Slide();

              document.querySelector('html').classList.add('wb-transitioning');

              if (typeof a11y != 'undefined' && !a11y.data.anims) resolve();
              else transition.in().then(resolve);
            });
          },

          beforeEnter(data) {
            const wpAdminBar = document.querySelector('#wpadminbar');
            const offset = wpAdminBar ? wpAdminBar.clientHeight : 0;

            WbScrollTo.scrollTo({ element: 'body', duration: 0, offset });
          },

          enter(data) {
            return new Promise((resolve) => {
              const transition = new Slide();

              if (typeof a11y != 'undefined' && !a11y.data.anims) resolve();
              else transition.out().then(resolve);
            });
          },

          after(data) {
            return new Promise((resolve) => {
              document.querySelector('html').classList.remove('wb-transitioning');
              WbScrollTo.scrollToAnchor().then(resolve);
            });
          },
        },
      ],

      prevent: function (data) {
        const isClickEvent = data.event.type === 'click';
        const currentUrl = window.location.href.split('#')[0];
        const destinationIsCurrent = currentUrl === data.href;

        if (isClickEvent && destinationIsCurrent) {
          data.event.preventDefault();
          const wpAdminBar = document.querySelector('#wpadminbar');
          const offset = wpAdminBar ? wpAdminBar.clientHeight : 0;
          WbScrollTo.scrollTo({ element: 'body', offset });
        }

        if (
          data.el.classList.contains('ab-item') ||
          data.el.classList.contains('data-barba-prevent')
        ) {
          return true;
        }
      },
    });

    barba.hooks.afterLeave((data) => {
      return new Promise((resolve) => {
        _this.barbaAfterLeave(data).then(resolve);
      });
    });

    barba.hooks.beforeEnter((data) => {
      return new Promise((resolve) => {
        data?.current?.container?.remove();
        _this.barbaBeforeEnter(data).then(resolve);
      });
    });

    /**
     * Use this hook for Google Analytics
     * Remove if using GTM and configure history change - https://bookstack.webitinteractive.ca/books/gtm---barbajs
     */
    // barba.hooks.after(function (data) {
    //   if (typeof gtag != 'undefined') {
    //     gtag('config', 'UA-XXXXXXXXX-X', { 'page_path': data.next.url.path });
    //   } else if (typeof ga != 'undefined') {
    //     ga('set', 'page', data.next.url.path);
    //     ga('send', 'pageview');
    //   }
    // });
  }

  barbaAfterLeave(data) {
    const _this = this;

    return new Promise((resolve) => {
      WbMasthead.closeMenu();
      _this.updateExternalScripts(data);
      _this.updateNavs(data);

      resolve();
    });
  }

  barbaBeforeEnter(data) {
    const _this = this;

    return new Promise((resolve) => {
      _this
        .initAllFunctions()
        .then(() => {
          WbMasthead.resize(true);
          document.querySelectorAll('html, body').forEach((e) => e.classList.remove('--locked'));
          document.dispatchEvent(new Event('_page_loaded'));

          document.querySelector('html').focus();
        })
        .then(resolve);
    });
  }

  updateExternalScripts(data) {
    const nextContainer = data.next.container.parentNode.parentNode;
    const currentRef = document.querySelector('#page');
    const newRef = nextContainer.querySelector('#page');
    const newScripts = newRef
      ._nextAll('script, link')
      ._not(keepScripts)
      ._not('script[id^="gform"]');
    const currentScripts = currentRef._nextAll('script, link')._not(keepScripts);
    const gformScripts = nextContainer.querySelectorAll('script[id^="gform"]');

    // Change meta tags and yoast graph
    document.querySelectorAll('head meta')?._remove();
    document
      .querySelector('head')
      ?._prependAll(nextContainer.querySelectorAll('meta:not([itemprop]'));
    document
      .querySelector('.yoast-schema-graph')
      ?.replaceWith(nextContainer.querySelector('.yoast-schema-graph'));

    // Remove duplicate recaptcha scripts from head
    document.querySelectorAll('script[src^="https://www.gstatic.com/recaptcha/"]')?._remove();

    // Remove plugin-specific scripts from the old DOM
    currentScripts.forEach((e) => {
      e._remove();
    });

    // Add plugin-specific scripts to the new DOM
    const scripts = new Set([...gformScripts, ...newScripts]);
    this.createScripts(scripts, currentRef);
    this.fixGravityFormsScripts();

    // Dispatch DOMContentLoaded for plugins and ReCaptcha init
    document.dispatchEvent(new Event('DOMContentLoaded'));
  }

  updateNavs(data) {
    const nextContainer = data.next.container.parentNode.parentNode;
    const nextAdminBarEdit = nextContainer.querySelector('#wp-admin-bar-edit');

    if (nextAdminBarEdit) {
      const adminBarEdit = document.querySelector('#wp-admin-bar-edit');

      if (adminBarEdit != null) {
        adminBarEdit.replaceWith(nextAdminBarEdit);
      } else {
        const nextAdminBar = nextContainer.querySelector('#wp-admin-bar-root-default');
        const adminBar = document.querySelector('#wp-admin-bar-root-default');
        adminBar.replaceWith(nextAdminBar);
      }
    }

    // Replace masthead and mastfoot when switching language
    if (typeof data.trigger !== 'string' && data.trigger.classList.contains('language-switcher')) {
      const lang = data.trigger.getAttribute('data-lang');
      ajax_obj.lang = lang;
      setCookie('wp-wpml_current_language', lang, 120);

      document.querySelector('#masthead').replaceWith(nextContainer.querySelector('#masthead'));
      document.querySelector('#mastfoot').replaceWith(nextContainer.querySelector('#mastfoot'));
      document.querySelector('#wb-alert')?.replaceWith(nextContainer.querySelector('#wb-alert'));
      document
        .querySelector('#cookie-banner')
        ?.replaceWith(nextContainer.querySelector('#cookie-banner'));
      document
        .querySelector('#wb-search-overlay')
        ?.replaceWith(nextContainer.querySelector('#wb-search-overlay'));
    } else {
      // Replace masthead and mastfoot navs
      const currentNavs = [...document.querySelectorAll('nav')].filter(function (e) {
        return !e.closest('main');
      });
      const nextNavs = [...nextContainer.querySelectorAll('nav')].filter(function (e) {
        return !e.closest('main');
      });

      nextNavs.forEach((e, i) => {
        currentNavs[i].replaceWith(e);
      });

      document.querySelector('.wb-header--open')?.classList.remove('wb-header--open');
    }
  }

  createScripts(scripts, ref) {
    scripts.forEach((e) => {
      const scriptCode = e.innerHTML;
      const newScript = document.createElement('script');

      Array.from(e.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));

      if (scriptCode) newScript.appendChild(document.createTextNode(scriptCode));

      document.querySelector('body')._insertAfter(newScript, ref);
    });
  }

  fixGravityFormsScripts() {
    if (typeof gform != 'undefined') gform.domLoaded = true;
  }
}
